import type {
  Part,
  PartAction,
  PartExpand,
  PartSeparator,
  SeparatedPart,
} from 'schemaDefinition/types';

type ProperPart = Exclude<Part, PartExpand>;
/**
 * Flattens parts, i.e.:
 * - removes separators
 * - flattens arrays
 * - expands expand parts (includes all branches, including default), but does not include the expand itself
 *   and excludes duplicates
 * Will NOT recurse through sub-schemas.
 * @param parts
 * @returns
 */
export const getPartsFlattended = (parts: SeparatedPart[]): ProperPart[] => {
  const noArrayParts = parts.reduce<(PartSeparator | PartAction | Part)[]>(
    (acc, p) => {
      if (Array.isArray(p)) {
        acc.push(...p);
      } else {
        acc.push(p);
      }
      return acc;
    },
    [],
  );

  const noSeparatorParts = noArrayParts.filter(p => p.type !== 'separator');
  const noActionParts = noSeparatorParts.filter(p => p.type !== 'action');

  const noExpandParts = noActionParts.reduce<ProperPart[]>((acc, p) => {
    if (p.type === 'expand') {
      acc.push(...getExpandPartsMerged(p));
    } else {
      acc.push(p as ProperPart);
    }
    return acc;
  }, []);

  return noExpandParts;
};

const getExpandPartsMerged = (expand: PartExpand): ProperPart[] => {
  return (
    [
      ...expand.when.map(w => getPartsFlattended(w.parts)),
      ...(expand.default ? getPartsFlattended(expand.default) : []),
    ].flat() as ProperPart[]
  ).reduce<ProperPart[]>(
    (acc, part) => (acc.includes(part) ? acc : [...acc, part]), // Consider comparing names instead of objects
    [],
  );
};
